import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import {
  adminGrowthSnapshotCreateAction,
  adminGrowthSnapshotGetAction,
  adminGrowthSnapshotUpdateAction
} from "../Action/action";
import config from "../coreFIles/config";
import { getAdminAuthToken } from "../coreFIles/auth";
import CKEditorWithSource from "./CKEditorWithSource";

function buildApiUrl(endpoint) {
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

class CkUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.controller = new AbortController();
  }

  abort() {
    try {
      this.controller.abort();
    } catch (e) {}
  }

  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("upload", file);
    const token = getAdminAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
      headers,
      withCredentials: true,
      signal: this.controller.signal
    });
    const url = resp?.data?.url;
    if (!url) throw new Error("Upload failed");
    return { default: resolvePublicUrl(url) };
  }
}

function CkUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CkUploadAdapter(loader);
}

const editorContainerStyle = { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" };
const editorConfig = {
  extraPlugins: [CkUploadAdapterPlugin],
  toolbar: [
    "heading",
    "|",
    "bold",
    "italic",
    "link",
    "bulletedList",
    "numberedList",
    "|",
    "undo",
    "redo",
    "|",
    "imageUpload"
  ]
};

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

function isImageFile(file) {
  if (!file) return false;
  const type = String(file.type || "").toLowerCase();
  return type.startsWith("image/") || /\.(png|jpg|jpeg|webp|gif)$/i.test(file.name || "");
}

export default function GrowthSnapshotForm({ mode = "create" }) {
  const isEdit = mode === "edit";
  const { snapshotId } = useParams();
  const navigate = useNavigate();
  const iconInputRef = useRef(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    heading_title: "",
    description_title: "",
    short_description: "",
    description: "",
    status: 1
  });
  const [existingIcon, setExistingIcon] = useState("");
  const [iconFile, setIconFile] = useState(null);

  useEffect(() => {
    if (!isEdit || !snapshotId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await adminGrowthSnapshotGetAction(snapshotId);
        if (cancelled) return;
        if (!res?.success || !res.data) {
          toast.error(res?.message || "Not found", { toastId: "gs-form-load" });
          navigate("/growth-snapshots");
          return;
        }
        const d = res.data;
        setForm({
          title: d.title || "",
          subtitle: d.subtitle || "",
          heading_title: d.heading_title || "",
          description_title: d.description_title || "",
          short_description: d.short_description || "",
          description: d.description || "",
          status: Number(d.status) === 0 ? 0 : 1
        });
        setExistingIcon(d.icon || "");
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.message || e?.message || "Load failed", {
            toastId: "gs-form-load"
          });
          navigate("/growth-snapshots");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, snapshotId, navigate]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!String(form.title || "").trim()) {
      toast.error("Title is required", { toastId: "gs-form-save" });
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title.trim());
      fd.append("subtitle", form.subtitle || "");
      fd.append("heading_title", form.heading_title || "");
      fd.append("description_title", form.description_title || "");
      fd.append("short_description", form.short_description || "");
      fd.append("description", form.description || "");
      fd.append("status", String(Number(form.status) === 1 ? 1 : 0));
      if (iconFile) fd.append("icon", iconFile);

      const res = isEdit
        ? await adminGrowthSnapshotUpdateAction(snapshotId, fd)
        : await adminGrowthSnapshotCreateAction(fd);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      toast.success(isEdit ? "Growth snapshot updated" : "Growth snapshot created", {
        toastId: "gs-form-save"
      });
      const id = res?.data?.id || snapshotId;
      navigate(id ? `/growth-snapshots/${id}/sections` : "/growth-snapshots");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed", {
        toastId: "gs-form-save"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="page-content content">
      <div className="page-header-row toolbar">
        <div>
          <h2 className="page-title">{isEdit ? "Edit Growth Snapshot" : "Add Growth Snapshot"}</h2>
          <p className="page-subtitle">Family Growth Snapshots — title and description.</p>
        </div>
        <Link className="btn btn-outline btn-sm" to="/growth-snapshots">
          ← Back to list
        </Link>
      </div>

      <form className="card" onSubmit={onSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Title *</label>
            <input
              className="form-control"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Family Growth Snapshots"
            />
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              className="form-control"
              value={form.status}
              onChange={(e) => setField("status", Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Subtitle</label>
          <input
            className="form-control"
            value={form.subtitle}
            onChange={(e) => setField("subtitle", e.target.value)}
            placeholder="Optional subtitle"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Heading title</label>
            <input
              className="form-control"
              value={form.heading_title}
              onChange={(e) => setField("heading_title", e.target.value)}
              placeholder="Heading title"
            />
          </div>
          <div className="form-group">
            <label>Description title</label>
            <input
              className="form-control"
              value={form.description_title}
              onChange={(e) => setField("description_title", e.target.value)}
              placeholder="Description title"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Short description</label>
          <CKEditorWithSource
            heightClassName="ckeditor-height-200"
            containerStyle={editorContainerStyle}
            config={editorConfig}
            data={form.short_description}
            onChange={(html) => setField("short_description", html)}
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <CKEditorWithSource
            heightClassName="ckeditor-height-400"
            containerStyle={editorContainerStyle}
            config={editorConfig}
            data={form.description}
            onChange={(html) => setField("description", html)}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Icon</label>
            <div className="upload-zone" role="button" tabIndex={0} onClick={() => iconInputRef.current?.click?.()} onKeyDown={(e) => { if (e.key === "Enter") iconInputRef.current?.click?.(); }} >
              <div className="upload-zone-icon">
                <span style={{ color: "var(--primary)", display: "inline-flex" }} aria-hidden="true">
                  <svg width="40" height="40" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="3" />
                    <circle cx="9" cy="9" r="2.5" />
                    <path d="M3 16l5-5 4 4 3-3 6 6" />
                  </svg>
                </span>
              </div>
              <p>{iconFile ? iconFile.name : "Click to upload icon"}</p>
              <p className="upload-zone-hint">PNG, JPG, WEBP, GIF</p>
            </div>
            <input ref={iconInputRef} type="file" accept="image/*,.png,.jpg,.jpeg,.webp,.gif" style={{ display: "none" }} onChange={(e) => {
                const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                if (file && !isImageFile(file)) {
                  setIconFile(null);
                  toast.error("Icon must be an image file", { toastId: "gs-icon" });
                  e.target.value = "";
                  return;
                }
                setIconFile(file);
              }}
            />
            {existingIcon && !iconFile ? (
              <div style={{ marginTop: 10 }}>
                <img src={resolvePublicUrl(existingIcon)} alt="Current icon" style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 8 }} />
              </div>
            ) : null}
          </div>
        </div>

        <div className="form-actions">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Update" : "Save"}
          </button>
          <Link className="btn btn-outline" to="/growth-snapshots">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
